"use client";

import React, { useState } from "react";
import { ChevronRight, Database, Plus, Table2, Trash2 } from "lucide-react";
import type { DbColumn, DbSchema, DbTable, PrimitiveType } from "./types";
import {
  emptyDbColumn,
  emptyDbTable,
  inputCls,
  labelCls,
  PRIMITIVE_TYPES,
} from "./constants";

// ─── Column Row ────────────────────────────────────────────────────────────────

function ColumnRow({
  column,
  onChange,
  onDelete,
}: {
  column: DbColumn;
  onChange: (c: DbColumn) => void;
  onDelete: () => void;
}) {
  const set = <K extends keyof DbColumn>(key: K, val: DbColumn[K]) =>
    onChange({ ...column, [key]: val });

  return (
    <div className="grid grid-cols-[1fr_140px_90px_auto] items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 dark:border-white/10 dark:bg-zinc-900">
      <input
        className="w-full bg-transparent text-sm text-gray-700 outline-none placeholder:text-gray-300 dark:text-gray-300"
        placeholder="spaltenname"
        value={column.name}
        onChange={(e) => set("name", e.target.value)}
      />
      <select
        className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1 font-mono text-xs text-gray-600 outline-none dark:border-white/10 dark:bg-zinc-800 dark:text-gray-300"
        value={column.type}
        onChange={(e) => set("type", e.target.value as PrimitiveType)}
      >
        {PRIMITIVE_TYPES.map((pt) => (
          <option key={pt} value={pt}>
            {pt}
          </option>
        ))}
      </select>
      <label className="flex cursor-pointer items-center justify-center gap-1.5 text-xs text-gray-500">
        <input
          type="checkbox"
          checked={column.nullable}
          onChange={(e) => set("nullable", e.target.checked)}
          className="h-3.5 w-3.5"
        />
        nullable
      </label>
      <button onClick={onDelete} className="text-gray-300 hover:text-red-400">
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// ─── Table Editor ──────────────────────────────────────────────────────────────

function TableEditor({
  table,
  onChange,
  onDelete,
}: {
  table: DbTable;
  onChange: (t: DbTable) => void;
  onDelete: () => void;
}) {
  const set = <K extends keyof DbTable>(key: K, val: DbTable[K]) =>
    onChange({ ...table, [key]: val });

  const addColumn = () => {
    const c = emptyDbColumn();
    onChange({ ...table, columns: [...table.columns, c] });
  };

  const updateColumn = (updated: DbColumn) =>
    onChange({
      ...table,
      columns: table.columns.map((c) => (c.id === updated.id ? updated : c)),
    });

  const deleteColumn = (id: string) =>
    onChange({ ...table, columns: table.columns.filter((c) => c.id !== id) });

  return (
    <div className="space-y-6">
      {/* Table name */}
      <div className="flex items-end gap-3">
        <div className="flex-1">
          <label className={labelCls}>Tabellenname</label>
          <input
            autoFocus
            className={inputCls}
            placeholder="z.B. users"
            value={table.name}
            onChange={(e) => set("name", e.target.value)}
          />
        </div>
        <button
          onClick={onDelete}
          className="mb-0.5 rounded-xl p-2.5 text-gray-400 hover:bg-red-50 hover:text-red-400 dark:hover:bg-red-500/10"
          title="Tabelle löschen"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      {/* Columns */}
      <div>
        <div className="mb-3 flex items-center justify-between">
          <label className={labelCls}>Spalten</label>
          <button
            onClick={addColumn}
            className="flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-600"
          >
            <Plus className="h-3 w-3" /> Spalte hinzufügen
          </button>
        </div>

        {table.columns.length > 0 && (
          <div className="mb-1 grid grid-cols-[1fr_140px_90px_auto] gap-2 px-3 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
            <span>Name</span>
            <span>Typ</span>
            <span>Nullable</span>
            <span />
          </div>
        )}

        <div className="space-y-1.5">
          {table.columns.length === 0 && (
            <p className="text-xs text-gray-400">Noch keine Spalten.</p>
          )}
          {table.columns.map((col) => (
            <ColumnRow
              key={col.id}
              column={col}
              onChange={updateColumn}
              onDelete={() => deleteColumn(col.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── DB Schema Editor ──────────────────────────────────────────────────────────

export function DbSchemaEditor({
  schema,
  onChange,
}: {
  schema: DbSchema;
  onChange: (s: DbSchema) => void;
}) {
  const [selectedTableId, setSelectedTableId] = useState<string | null>(
    schema.tables[0]?.id ?? null,
  );

  const selectedTable =
    schema.tables.find((t) => t.id === selectedTableId) ?? null;

  const addTable = () => {
    const t = emptyDbTable();
    onChange({ ...schema, tables: [...schema.tables, t] });
    setSelectedTableId(t.id);
  };

  const updateTable = (updated: DbTable) =>
    onChange({
      ...schema,
      tables: schema.tables.map((t) => (t.id === updated.id ? updated : t)),
    });

  const deleteTable = (id: string) => {
    const remaining = schema.tables.filter((t) => t.id !== id);
    onChange({ ...schema, tables: remaining });
    if (selectedTableId === id) setSelectedTableId(remaining[0]?.id ?? null);
  };

  return (
    <div className="h-155 flex overflow-hidden">
      {/* Sidebar: table list */}
      <div className="flex w-60 shrink-0 flex-col border-r border-gray-200 bg-gray-50 dark:border-white/10 dark:bg-zinc-900">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-white/10">
          <div className="flex items-center gap-2">
            <Database className="h-4 w-4 text-gray-400" />
            <span className="text-sm font-semibold text-gray-600 dark:text-gray-300">
              Tabellen
            </span>
          </div>
          <button
            onClick={addTable}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-200 hover:text-gray-600 dark:hover:bg-white/10"
            title="Tabelle hinzufügen"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          {schema.tables.length === 0 && (
            <p className="px-2 py-4 text-center text-xs text-gray-400">
              Noch keine Tabellen.
            </p>
          )}
          {schema.tables.map((t) => (
            <button
              key={t.id}
              onClick={() => setSelectedTableId(t.id)}
              className={`group flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm transition ${
                selectedTableId === t.id
                  ? "bg-amber-500/10 text-amber-600 dark:text-amber-400"
                  : "text-gray-600 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-white/5"
              }`}
            >
              <Table2 className="h-3.5 w-3.5 shrink-0" />
              <span className="flex-1 truncate font-medium">
                {t.name || (
                  <span className="italic text-gray-400">Kein Name</span>
                )}
              </span>
              <span className="text-[10px] text-gray-400">
                {t.columns.length}
              </span>
              <ChevronRight className="h-3 w-3 shrink-0 opacity-40" />
            </button>
          ))}
        </div>
      </div>

      {/* Main area: table editor */}
      <div className="flex-1 overflow-y-auto p-6">
        {!selectedTable ? (
          <div className="flex h-full items-center justify-center">
            <div className="text-center text-gray-400">
              <Database className="mx-auto mb-3 h-10 w-10 opacity-30" />
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Tabelle auswählen oder neu erstellen
              </p>
              <p className="mt-1 text-xs">
                Klicke links auf + um eine Tabelle anzulegen.
              </p>
            </div>
          </div>
        ) : (
          <TableEditor
            table={selectedTable}
            onChange={updateTable}
            onDelete={() => deleteTable(selectedTable.id)}
          />
        )}
      </div>
    </div>
  );
}
